/**
 * Service Management Service
 * サービス管理サービス
 * 
 * Week 4: サービス管理UI実装
 */

import logger from '../utils/logger';
import {
  Service,
  ServiceCategory,
  ServiceBundle,
  ServiceStatistics,
  ServiceValidation,
  ServiceFormData,
  ServiceManagerState
} from '../types/service';
import { useStore } from '../store/useStore';

class ServiceManagementService {
  private static instance: ServiceManagementService;



  /**
   * StoreService[] を Service[] に変換する関数
   */
  private convertStoreServicesToServices(storeServices: Service[]): Service[] {
    return storeServices; // 型が同じなので直接返す
  }
  private initialized = false;
  private unsubscribe?: () => void;
  private data: ServiceManagerState = {
    services: [],
    categories: [],
    bundles: [],
    selectedService: null,
    loading: false,
    error: null,
    filters: {},
    sortBy: 'name',
    sortOrder: 'asc'
  };

  static getInstance(): ServiceManagementService {
    if (!ServiceManagementService.instance) {
      ServiceManagementService.instance = new ServiceManagementService();
    }
    return ServiceManagementService.instance;
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      this.data.loading = true;

      // Storeからデータを取得
      await this.loadFromStore();

      // Store変更の監視を開始
      this.subscribeToStoreChanges();

      // カテゴリーはデフォルトを使用
      if (this.data.categories.length === 0) {
        this.data.categories = this.getDefaultCategories();
      }

      // バンドルはデフォルトを使用
      if (this.data.bundles.length === 0) {
        this.data.bundles = this.getDefaultBundles();
      }
      
      this.initialized = true;
      this.data.loading = false;

      logger.log('✅ ServiceManagementService initialized with store integration');

    } catch (error) {
      if (import.meta.env.DEV) {
        logger.error('❌ ServiceManagementService initialization failed:', error);
      }
      this.data.error = 'サービス管理システムの初期化に失敗しました';
      this.data.loading = false;
      // フォールバック: デフォルトデータを使用
      this.data.services = this.getDefaultServices();
      this.data.categories = this.getDefaultCategories();
      this.data.bundles = this.getDefaultBundles();
      this.initialized = true;
    }
  }

  private async loadFromStore(): Promise<void> {
    const store = useStore.getState();
    const storeServices = store.services;

    if (storeServices && storeServices.length > 0) {
      this.data.services = this.convertStoreServicesToServices(storeServices);
      logger.log('📦 Loaded services from store:', this.data.services.length);
    } else {
      // Storeにデータがない場合はデフォルトを設定
      this.data.services = this.getDefaultServices();
      // デフォルトデータをStoreに保存
      await this.saveToStore();
    }
  }

  private subscribeToStoreChanges(): void {
    // Store変更の監視
    this.unsubscribe = useStore.subscribe((state) => {
      const services = state.services;
      if (services && JSON.stringify(services) !== JSON.stringify(this.data.services)) {
        this.data.services = this.convertStoreServicesToServices(services);
        logger.log('🔄 Services updated from store');
      }
    });
  }

  private async saveToStore(): Promise<void> {
    const store = useStore.getState();
    if (store.setServices) {
      store.setServices(this.data.services);
    }
  }

  refresh(): void {
    // 外部から強制的に再読み込み
    this.loadFromStore();
  }

  cleanup(): void {
    // サービスのクリーンアップ時に監視を解除
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = undefined;
    }
    this.initialized = false;
  }

  private getDefaultCategories(): ServiceCategory[] {
    return [
      {
        id: 'cat-head-spa',
        name: 'ヘッドスパ',
        description: 'ドライヘッドスパとアロマヘッドスパ',
        displayOrder: 1,
        isActive: true,
        color: '#6c5ce7',
        icon: 'spa'
      },
      {
        id: 'cat-hair-care',
        name: 'ヘアケア',
        description: 'カット、カラー、パーマなど',
        displayOrder: 2,
        isActive: true,
        color: '#fd79a8',
        icon: 'content_cut'
      },
      {
        id: 'cat-relaxation',
        name: 'リラクゼーション',
        description: 'マッサージ、アロマセラピーなど',
        displayOrder: 3,
        isActive: true,
        color: '#00b894',
        icon: 'self_improvement'
      },
      {
        id: 'cat-combination',
        name: 'セットメニュー',
        description: '複数メニューの組み合わせ',
        displayOrder: 4,
        isActive: true,
        color: '#fdcb6e',
        icon: 'playlist_add'
      }
    ];
  }

  private getDefaultServices(): Service[] {
    const now = new Date();
    
    return [
      {
        id: 'service-dry-head-spa-60',
        categoryId: 'cat-head-spa',
        name: 'ドライヘッドスパ 60分',
        description: '頭皮と首肩のマッサージで深いリラクゼーションを提供',
        duration: 60,
        basePrice: 6000,
        taxRate: 10,
        isActive: true,
        displayOrder: 1,
        availableStaff: ['staff1', 'staff2', 'staff3'],
        requiredSkillLevel: 'intermediate',
        advanceBookingDays: 30,
        minBookingNotice: 60,
        bufferTime: 10,
        options: [
          {
            id: 'opt-aroma',
            name: 'アロマオイル追加',
            description: 'お好みのアロマオイルを使用',
            additionalPrice: 500,
            additionalDuration: 5,
            isRequired: false,
            isActive: true,
            displayOrder: 1
          }
        ],
        tags: ['人気', 'リラクゼーション', '初回おすすめ'],
        popularity: 95,
        seasonality: 'all',
        targetGender: 'unisex',
        ageRange: { min: 20, max: 70 },
        requiredResources: [
          {
            type: 'equipment',
            resourceId: 'massage-chair-1',
            quantity: 1,
            isRequired: true
          }
        ],
        roomRequirement: 'private',
        statistics: {
          totalBookings: 450,
          revenue: 2700000,
          averageRating: 4.8,
          cancellationRate: 2.1,
          noShowRate: 0.8
        },
        createdAt: now,
        updatedAt: now
      },
      {
        id: 'service-dry-head-spa-90',
        categoryId: 'cat-head-spa',
        name: 'ドライヘッドスパ 90分',
        description: 'じっくり時間をかけた極上のヘッドスパ体験',
        duration: 90,
        basePrice: 8500,
        taxRate: 10,
        isActive: true,
        displayOrder: 2,
        availableStaff: ['staff1', 'staff2'],
        requiredSkillLevel: 'advanced',
        advanceBookingDays: 30,
        minBookingNotice: 120,
        bufferTime: 15,
        options: [
          {
            id: 'opt-premium-aroma',
            name: 'プレミアムアロマ',
            description: '高級アロマオイルを使用',
            additionalPrice: 1000,
            additionalDuration: 0,
            isRequired: false,
            isActive: true,
            displayOrder: 1
          }
        ],
        tags: ['プレミアム', 'じっくり', 'デート前'],
        popularity: 78,
        seasonality: 'all',
        targetGender: 'unisex',
        ageRange: { min: 25, max: 65 },
        requiredResources: [
          {
            type: 'equipment',
            resourceId: 'premium-chair-1',
            quantity: 1,
            isRequired: true
          }
        ],
        roomRequirement: 'private',
        statistics: {
          totalBookings: 280,
          revenue: 2380000,
          averageRating: 4.9,
          cancellationRate: 1.8,
          noShowRate: 0.5
        },
        createdAt: now,
        updatedAt: now
      },
      {
        id: 'service-aroma-head-spa-60',
        categoryId: 'cat-head-spa',
        name: 'アロマヘッドスパ 60分',
        description: '厳選アロマオイルを使用した贅沢なヘッドスパ',
        duration: 60,
        basePrice: 7000,
        taxRate: 10,
        isActive: true,
        displayOrder: 3,
        availableStaff: ['staff1', 'staff3'],
        requiredSkillLevel: 'intermediate',
        advanceBookingDays: 30,
        minBookingNotice: 60,
        bufferTime: 10,
        options: [
          {
            id: 'opt-hot-stone',
            name: 'ホットストーン',
            description: '温かい石を使った追加マッサージ',
            additionalPrice: 800,
            additionalDuration: 10,
            isRequired: false,
            isActive: true,
            displayOrder: 1
          }
        ],
        tags: ['アロマ', '女性人気', 'デトックス'],
        popularity: 82,
        seasonality: 'all',
        targetGender: 'female',
        ageRange: { min: 20, max: 60 },
        requiredResources: [
          {
            type: 'product',
            resourceId: 'aroma-oil-set',
            quantity: 1,
            isRequired: true
          }
        ],
        roomRequirement: 'private',
        statistics: {
          totalBookings: 320,
          revenue: 2240000,
          averageRating: 4.7,
          cancellationRate: 2.5,
          noShowRate: 1.0
        },
        createdAt: now,
        updatedAt: now
      },
      {
        id: 'service-cut-head-spa-combo',
        categoryId: 'cat-combination',
        name: 'カット + ヘッドスパセット',
        description: 'ヘアカットとドライヘッドスパのお得なセット',
        duration: 120,
        basePrice: 9500,
        taxRate: 10,
        isActive: true,
        displayOrder: 4,
        availableStaff: ['staff2', 'staff4'],
        requiredSkillLevel: 'advanced',
        advanceBookingDays: 45,
        minBookingNotice: 180,
        bufferTime: 20,
        options: [
          {
            id: 'opt-shampoo-upgrade',
            name: 'プレミアムシャンプー',
            description: 'オーガニックシャンプーに変更',
            additionalPrice: 600,
            additionalDuration: 0,
            isRequired: false,
            isActive: true,
            displayOrder: 1
          }
        ],
        tags: ['セット割', '男性人気', 'トータルケア'],
        popularity: 67,
        seasonality: 'all',
        targetGender: 'unisex',
        ageRange: { min: 18, max: 65 },
        requiredResources: [
          {
            type: 'equipment',
            resourceId: 'cutting-station-1',
            quantity: 1,
            isRequired: true
          },
          {
            type: 'equipment',
            resourceId: 'massage-chair-1',
            quantity: 1,
            isRequired: true
          }
        ],
        roomRequirement: 'shared',
        statistics: {
          totalBookings: 180,
          revenue: 1710000,
          averageRating: 4.6,
          cancellationRate: 3.2,
          noShowRate: 1.5
        },
        createdAt: now,
        updatedAt: now
      }
    ];
  }

  private getDefaultBundles(): ServiceBundle[] {
    const now = new Date();
    
    return [
      {
        id: 'bundle-relax-premium',
        name: 'プレミアムリラックスコース',
        description: 'ドライヘッドスパ90分 + アロマトリートメント',
        serviceIds: ['service-dry-head-spa-90', 'service-aroma-head-spa-60'],
        bundlePrice: 14000,
        totalDuration: 150,
        discount: 1500,
        discountRate: 9.7,
        isActive: true,
        validFrom: now,
        validTo: new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000), // 1年後
        minAdvanceBooking: 240,
        maxBookingsPerDay: 3,
        targetCustomerTier: 'gold'
      }
    ];
  }

  // データ取得メソッド
  getServices(): Service[] {
    return [...this.data.services];
  }

  getCategories(): ServiceCategory[] {
    return [...this.data.categories];
  }

  getBundles(): ServiceBundle[] {
    return [...this.data.bundles];
  }

  getServiceById(id: string): Service | null {
    return this.data.services.find(s => s.id === id) || null;
  }

  getServicesByCategory(categoryId: string): Service[] {
    return this.data.services.filter(s => s.categoryId === categoryId && s.isActive);
  }

  // フィルタリング・ソート
  getFilteredServices(filters?: {
    categoryId?: string;
    isActive?: boolean;
    staffId?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
    minDuration?: number;
    maxDuration?: number;
  }): Service[] {
    let filtered = [...this.data.services];

    if (filters) {
      if (filters.categoryId) {
        filtered = filtered.filter(s => s.categoryId === filters.categoryId);
      }
      if (filters.isActive !== undefined) {
        filtered = filtered.filter(s => s.isActive === filters.isActive);
      }
      if (filters.staffId) {
        filtered = filtered.filter(s => s.availableStaff.includes(filters.staffId!));
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filtered = filtered.filter(s => 
          s.name.toLowerCase().includes(searchLower) ||
          s.description?.toLowerCase().includes(searchLower) ||
          s.tags.some(tag => tag.toLowerCase().includes(searchLower))
        );
      }
      if (filters.minPrice !== undefined) {
        filtered = filtered.filter(s => s.basePrice >= filters.minPrice!);
      }
      if (filters.maxPrice !== undefined) {
        filtered = filtered.filter(s => s.basePrice <= filters.maxPrice!);
      }
      if (filters.minDuration !== undefined) {
        filtered = filtered.filter(s => s.duration >= filters.minDuration!);
      }
      if (filters.maxDuration !== undefined) {
        filtered = filtered.filter(s => s.duration <= filters.maxDuration!);
      }
    }

    return filtered;
  }

  // サービス管理
  async createService(serviceData: ServiceFormData): Promise<Service> {
    const now = new Date();
    const service: Service = {
      id: `service-${Date.now()}`,
      ...serviceData,
      displayOrder: serviceData.displayOrder || 999,
      statistics: {
        totalBookings: 0,
        revenue: 0,
        averageRating: 0,
        cancellationRate: 0,
        noShowRate: 0
      },
      popularity: 0,
      seasonality: 'all',
      targetGender: 'unisex',
      requiredResources: [],
      createdAt: now,
      updatedAt: now
    };

    this.data.services.push(service);
    // Storeに保存
    await this.saveToStore();
    // サービス作成完了（本番環境では詳細ログを無効化）
    
    return service;
  }

  async updateService(id: string, updates: Partial<ServiceFormData>): Promise<Service> {
    const index = this.data.services.findIndex(s => s.id === id);
    if (index === -1) {
      throw new Error('サービスが見つかりません');
    }

    this.data.services[index] = {
      ...this.data.services[index],
      ...updates,
      updatedAt: new Date()
    };
    // Storeに保存
    await this.saveToStore();

    // サービス更新完了（本番環境では詳細ログを無効化）
    return this.data.services[index];
  }

  async deleteService(id: string): Promise<void> {
    const index = this.data.services.findIndex(s => s.id === id);
    if (index === -1) {
      throw new Error('サービスが見つかりません');
    }

    // ソフト削除（非アクティブ化）
    this.data.services[index].isActive = false;
    this.data.services[index].updatedAt = new Date();
    // Storeに保存
    await this.saveToStore();

    // サービス非有効化完了（本番環境では詳細ログを無効化）
  }

  // バリデーション
  validateService(serviceData: Partial<ServiceFormData>): ServiceValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!serviceData.name || serviceData.name.trim().length === 0) {
      errors.push('サービス名は必須です');
    }

    if (!serviceData.categoryId) {
      errors.push('カテゴリーの選択は必須です');
    }

    if (!serviceData.duration || serviceData.duration <= 0) {
      errors.push('所要時間は1分以上で入力してください');
    } else if (serviceData.duration > 480) { // 8時間
      warnings.push('所要時間が8時間を超えています');
    }

    if (!serviceData.basePrice || serviceData.basePrice <= 0) {
      errors.push('基本料金は0円より大きく入力してください');
    } else if (serviceData.basePrice > 100000) {
      warnings.push('基本料金が100,000円を超えています');
    }

    if (serviceData.minBookingNotice && serviceData.minBookingNotice > 7 * 24 * 60) {
      warnings.push('予約受付締切が7日を超えています');
    }

    if (serviceData.availableStaff && serviceData.availableStaff.length === 0) {
      warnings.push('担当可能スタッフが設定されていません');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  // 統計・分析
  getServiceStatistics(serviceId: string, period: { start: Date; end: Date }): ServiceStatistics {
    const service = this.getServiceById(serviceId);
    if (!service) {
      throw new Error('サービスが見つかりません');
    }

    // モックデータ - 実際の実装では予約データから計算
    return {
      serviceId,
      period,
      metrics: {
        totalBookings: service.statistics.totalBookings,
        totalRevenue: service.statistics.revenue,
        averagePrice: service.basePrice,
        averageDuration: service.duration,
        averageRating: service.statistics.averageRating,
        repeatCustomers: Math.floor(service.statistics.totalBookings * 0.3),
        newCustomers: Math.floor(service.statistics.totalBookings * 0.7),
        cancellations: Math.floor(service.statistics.totalBookings * service.statistics.cancellationRate / 100),
        noShows: Math.floor(service.statistics.totalBookings * service.statistics.noShowRate / 100),
        earlyCompletions: Math.floor(service.statistics.totalBookings * 0.05),
        extensions: Math.floor(service.statistics.totalBookings * 0.02)
      },
      trends: {
        bookingTrend: 'increasing',
        revenueTrend: 'increasing',
        satisfactionTrend: 'stable'
      },
      recommendations: [
        '価格を5%値上げしても需要は維持できそうです',
        '土日の予約枠を増やすことを検討してください',
        '定期顧客向けの割引プランの導入を検討してください'
      ]
    };
  }

  // 状態管理
  getState(): ServiceManagerState {
    return { ...this.data };
  }

  updateFilters(filters: Partial<ServiceManagerState['filters']>): void {
    this.data.filters = { ...this.data.filters, ...filters };
  }

  updateSorting(sortBy: ServiceManagerState['sortBy'], sortOrder: ServiceManagerState['sortOrder']): void {
    this.data.sortBy = sortBy;
    this.data.sortOrder = sortOrder;
  }

  selectService(service: Service | null): void {
    this.data.selectedService = service;
  }

  // ユーティリティ
  calculateServicePrice(
    serviceId: string,
    options: string[] = [],
    _staffId?: string,
    _customerTier?: string,
    _dateTime?: Date
  ): number {
    const service = this.getServiceById(serviceId);
    if (!service) return 0;

    let totalPrice = service.basePrice;

    // オプション料金を追加
    options.forEach(optionId => {
      const option = service.options.find(o => o.id === optionId);
      if (option && option.isActive) {
        totalPrice += option.additionalPrice;
      }
    });

    // 税込み価格を計算
    const taxAmount = Math.floor(totalPrice * service.taxRate / 100);
    return totalPrice + taxAmount;
  }

  calculateServiceDuration(serviceId: string, options: string[] = []): number {
    const service = this.getServiceById(serviceId);
    if (!service) return 0;

    let totalDuration = service.duration;

    // オプション時間を追加
    options.forEach(optionId => {
      const option = service.options.find(o => o.id === optionId);
      if (option && option.isActive) {
        totalDuration += option.additionalDuration;
      }
    });

    return totalDuration;
  }
}

export const serviceManagementService = ServiceManagementService.getInstance();
export default serviceManagementService;